// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

/**
 * Hanggent's Clerk configuration.
 *
 * Clerk is enabled only when a publishable key is configured. The runtime
 * value injected through `/env.js` (see docker/runtime-env.sh) wins over the
 * build-time one so a deployment can change it without rebuilding.
 */

export function getClerkPublishableKey(): string | undefined {
  const runtime =
    typeof window !== 'undefined'
      ? window.__ENV?.VITE_CLERK_PUBLISHABLE_KEY
      : undefined;
  return runtime || import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || undefined;
}

export function isClerkEnabled(): boolean {
  return Boolean(getClerkPublishableKey());
}

// A publishable key looks like pk_test_<base64("<frontend host>$")>.
function decodeClerkDomainFromKey(key?: string): string | undefined {
  if (!key) return undefined;
  const encoded = key.split('_')[2];
  if (!encoded) return undefined;
  try {
    const decoded = atob(encoded).replace(/\$/g, '');
    if (!decoded) return undefined;
    return decoded.startsWith('http') ? decoded : `https://${decoded}`;
  } catch {
    return undefined;
  }
}

/** Optional Clerk domain override; defaults to the host encoded in the key. */
export function getClerkDomain(): string | undefined {
  return (
    import.meta.env.VITE_CLERK_DOMAIN ||
    decodeClerkDomainFromKey(getClerkPublishableKey())
  );
}

/** Optional Clerk proxy URL; the runtime value wins over the build-time one. */
export function getClerkProxyUrl(): string | undefined {
  const runtime =
    typeof window !== 'undefined'
      ? window.__ENV?.VITE_CLERK_PROXY_URL
      : undefined;
  return runtime || import.meta.env.VITE_CLERK_PROXY_URL || undefined;
}

/** Optional Clerk JS URL, used to bypass custom-domain certificate problems. */
export function getClerkJsUrl(): string | undefined {
  return import.meta.env.VITE_CLERK_JS_URL || undefined;
}
